import { Customer, User } from "./types";

declare global {
  interface Window {
    pendo: {
      initialize: ({
        visitor,
        account,
      }: {
        visitor: Visitor;
        account: Account;
      }) => void;
    };
  }
}

export interface Visitor {
  id: string; // Required if user is logged in
  full_name: string; // Recommended if using Pendo Feedback
  email: string;
}

export interface Account {
  id: string; // Highly recommended
  salesforceId: string; // Customer specific field
  name: string;
}

export class PendoService {
  private pendo: any;

  constructor() {
    this.pendo = window.pendo;
  }

  public isPendoAvailable(): boolean {
    return !!this.pendo;
  }

  public async waitForPendo(): Promise<void> {
    return new Promise<void>((resolve) => {
      if (this.isPendoAvailable()) {
        resolve();
        return;
      }

      let attempts = 0;
      const maxAttempts = 10;

      const intervalId = setInterval(() => {
        attempts++;
        this.pendo = window.pendo;

        if (this.isPendoAvailable()) {
          clearInterval(intervalId);
          resolve();
          return;
        }

        if (attempts >= maxAttempts) {
          clearInterval(intervalId);
          console.warn(`Pendo not available after ${maxAttempts} attempts`);
          resolve(); // Still resolve to prevent blocking, but with a warning
        }
      }, 500);
    });
  }

  public async initialize(user: User, customer: Customer): Promise<void> {
    await this.waitForPendo();

    if (!this.isPendoAvailable()) {
      return;
    }

    this.pendo.initialize({
      visitor: {
        id: `accelerate_${user.id}`,
        full_name: `${user.firstName} ${user.lastName}`,
        email: user.email,
      } as Visitor,
      account: {
        id: `accelerate_${customer.customerDomainName}`,
        salesforceId: customer.velocityEhsCustomerNumber,
        name: customer.name,
      } as Account,
    });
  }

  public track(eventName: string, metadata?: any): void {
    if (!this.isPendoAvailable()) {
      console.warn("Pendo is not available.");
      return;
    }

    this.pendo.track(eventName, metadata);
  }
}
